/**
 * Maps raw HTTP requests to safe, low-cardinality telemetry: a stable
 * operation name (never the raw URL — those embed patient/session ids), a
 * status-code group, a duration bucket, and a normalized error code derived
 * only from transport state + status (never backend error text). Used by the
 * Axios taps in services/httpClient.ts.
 */

interface OperationRule {
  method?: string;
  /** Tested against the request path (query/hash already stripped). */
  re: RegExp;
  op: string;
}

// Ordered: more specific paths first (first match wins). Paths are the
// httpClient-relative url (e.g. '/account-mgmt/users/123'), matched after the
// '/api/v1' base. Mirrors clinician-portal/src/services/*Service.ts.
const OPERATION_RULES: readonly OperationRule[] = [
  // users
  {
    method: 'POST',
    re: /^\/account-mgmt\/users\/[^/]+\/reset-password$/,
    op: 'ResetUserPassword',
  },
  {
    method: 'POST',
    re: /^\/account-mgmt\/users\/[^/]+\/resend-invite$/,
    op: 'ResendUserInvite',
  },
  { method: 'POST', re: /^\/account-mgmt\/users\/[^/]+\/archive$/, op: 'ArchiveUser' },
  { method: 'POST', re: /^\/account-mgmt\/users\/[^/]+\/unarchive$/, op: 'UnarchiveUser' },
  { method: 'PATCH', re: /^\/account-mgmt\/users\/[^/]+\/role$/, op: 'ChangeUserRole' },
  { method: 'PATCH', re: /^\/account-mgmt\/users\/[^/]+\/email$/, op: 'ChangeUserEmail' },
  { method: 'PATCH', re: /^\/account-mgmt\/users\/[^/]+$/, op: 'UpdateUser' },
  { method: 'GET', re: /^\/account-mgmt\/users\/[^/]+$/, op: 'GetUser' },
  { method: 'POST', re: /^\/account-mgmt\/users$/, op: 'CreateUser' },
  { method: 'GET', re: /^\/account-mgmt\/users$/, op: 'ListUsers' },
  // account-mgmt reads that are NOT under /users. Without these they resolved to
  // 'Other', which made two different 403s indistinguishable in RUM and cost a long
  // investigation — /usages (clinic activity graph) and /tenants (removed in aec7e43a2).
  { method: 'GET', re: /^\/account-mgmt\/usages$/, op: 'ListUsages' },
  { method: 'GET', re: /^\/account-mgmt\/tenants$/, op: 'ListTenants' },
  // treatment plans
  { method: 'DELETE', re: /^\/treatment-plan\/assignment\/[^/]+$/, op: 'DeleteTreatmentPlan' },
  { method: 'POST', re: /^\/treatment-plan\/assignment$/, op: 'AssignTreatmentPlan' },
  { method: 'GET', re: /^\/treatment-plan\/assignment$/, op: 'ListTreatmentPlans' },
  { method: 'PATCH', re: /^\/treatment-plan\/template\/[^/]+$/, op: 'UpdateTemplate' },
  { method: 'DELETE', re: /^\/treatment-plan\/template\/[^/]+$/, op: 'DeleteTemplate' },
  { method: 'POST', re: /^\/treatment-plan\/template$/, op: 'CreateTemplate' },
  { method: 'GET', re: /^\/treatment-plan\/template$/, op: 'ListTemplates' },
  // movement results + streaming media
  { method: 'GET', re: /^\/movement\/results$/, op: 'LoadMovementResults' },
  { method: 'GET', re: /^\/streaming\/movement\/metrics\//, op: 'LoadMovementMetrics' },
  { method: 'GET', re: /^\/streaming\/movement\/media\//, op: 'LoadMovementMedia' },
  { method: 'GET', re: /^\/streaming\/active_session$/, op: 'GetActiveSession' },
  // cases
  { method: 'PATCH', re: /^\/case-id\/active$/, op: 'SetActiveCase' },
  { method: 'POST', re: /^\/case-id\/assignment$/, op: 'CreateCase' },
  { method: 'PATCH', re: /^\/case-id\/assignment$/, op: 'UpdateCase' },
  { method: 'GET', re: /^\/case-id\/assignment$/, op: 'ListCases' },
  // transcript + notes
  { method: 'POST', re: /^\/transcript\/create$/, op: 'CreateTranscript' },
  { method: 'POST', re: /^\/transcript\/process\//, op: 'ProcessTranscript' },
  { method: 'GET', re: /^\/transcript\/output\//, op: 'GetTranscriptOutput' },
  { method: 'POST', re: /^\/clinician-note$/, op: 'CreateClinicianNote' },
  { method: 'GET', re: /^\/clinician-note$/, op: 'ListClinicianNotes' },
  { method: 'PUT', re: /^\/clinician-note\/[^/]+$/, op: 'UpdateClinicianNote' },
  // session insights
  {
    method: 'POST',
    re: /^\/sessions\/[^/]+\/insights\/regenerate$/,
    op: 'RegenerateSessionInsights',
  },
  { method: 'PUT', re: /^\/sessions\/[^/]+\/insights$/, op: 'UpdateSessionInsights' },
  { method: 'GET', re: /^\/sessions\/[^/]+\/insights$/, op: 'LoadSessionInsights' },
  // emr
  { method: 'GET', re: /^\/emr\/adapters\/[^/]+$/, op: 'GetEmrAdapter' },
  { method: 'GET', re: /^\/emr\/adapters$/, op: 'ListEmrAdapters' },
  { method: 'GET', re: /^\/emr\/links/, op: 'GetEmrLinks' },
  { method: 'PUT', re: /^\/emr\/links\/[^/]+$/, op: 'PutEmrLink' },
  { method: 'POST', re: /^\/emr\/audit$/, op: 'PostEmrAudit' },
  // custom activities
  { method: 'POST', re: /^\/custom-activity\/upload-url$/, op: 'GetCustomActivityUploadUrl' },
  {
    method: 'POST',
    re: /^\/custom-activity\/generate-collateral$/,
    op: 'GenerateCustomActivityCollateral',
  },
  { method: 'PUT', re: /^\/custom-activity\/[^/]+$/, op: 'UpdateCustomActivity' },
  { method: 'DELETE', re: /^\/custom-activity\/[^/]+$/, op: 'DeleteCustomActivity' },
  { method: 'GET', re: /^\/custom-activity$/, op: 'ListCustomActivities' },
];

/**
 * Resolve a stable operation name for a request. Unknown/absolute (e.g. S3
 * presigned upload) URLs collapse to 'Other' — bounded cardinality, never the
 * raw URL.
 */
export function resolveApiOperation(
  method: string | undefined,
  url: string | undefined,
): string {
  if (!url) {
    return 'Other';
  }
  // Absolute URLs (e.g. S3 presigned PUT) are not our API surface.
  if (/^https?:\/\//i.test(url)) {
    return 'Other';
  }
  const path = (url.split('?')[0] ?? '').split('#')[0] ?? '';
  const m = (method || '').toUpperCase();
  for (const rule of OPERATION_RULES) {
    if (rule.method && rule.method !== m) {
      continue;
    }
    if (rule.re.test(path)) {
      return rule.op;
    }
  }
  return 'Other';
}

export type HttpStatusGroup = '2xx' | '3xx' | '4xx' | '5xx' | '0';

/** '0' means no HTTP response (network error / timeout / cancelled). */
export function httpStatusGroup(status: number | undefined): HttpStatusGroup {
  if (!status || status < 100) {
    return '0';
  }
  return `${Math.floor(status / 100)}xx` as HttpStatusGroup;
}

export type DurationBucket = '<300ms' | '300ms-1s' | '1-3s' | '3-10s' | '>10s';

export function durationBucket(ms: number): DurationBucket {
  if (ms < 300) {
    return '<300ms';
  }
  if (ms < 1000) {
    return '300ms-1s';
  }
  if (ms < 3000) {
    return '1-3s';
  }
  if (ms < 10000) {
    return '3-10s';
  }
  return '>10s';
}

export interface HttpErrorShape {
  cancelled?: boolean;
  /** axios error.code, e.g. 'ECONNABORTED'. */
  code?: string;
  status?: number;
  hasResponse?: boolean;
}

/**
 * Normalize an HTTP failure to a stable, PHI-free code. Derived ONLY from
 * transport state + status — never from backend error message/body.
 */
export function normalizeHttpError(input: HttpErrorShape): string {
  if (input.cancelled) {
    return 'CANCELLED';
  }
  if (input.code === 'ECONNABORTED') {
    return 'TIMEOUT';
  }
  if (input.status && input.status >= 100) {
    return `HTTP_${input.status}`;
  }
  if (input.hasResponse === false) {
    return 'NETWORK_ERROR';
  }
  return 'UNKNOWN_ERROR';
}

/**
 * Whether a failed response should be reported as `portal.api_failed`.
 *
 * A caller can declare statuses that are a NORMAL outcome for that specific request via
 * `telemetryExpectedStatuses`. The motivating case is the movement-media lookup, which probes the
 * current S3 prefix and then the legacy one: a 404 on the first attempt is a step of the lookup,
 * not a failure, and reporting it doubled every media-less movement in RUM.
 *
 * Deliberately narrow — it matches only an EXACT status the caller named on that call. It is not
 * a way to silence a status globally: the fallback request carries no list, so a movement whose
 * media is genuinely missing still reports exactly one failure.
 */
export function shouldRecordApiFailure(
  status: number | undefined,
  expectedStatuses: number[] | undefined,
): boolean {
  // A response-less failure (network error, timeout, cancellation) has no status, so it can never
  // match and is never suppressed — which is the behaviour we want, and it falls out of the same
  // expression. An explicit `status === undefined` guard was tried and removed: `includes` already
  // returns false for it, so the branch could not change any outcome and no test could kill it.
  return !(expectedStatuses ?? []).includes(status as number);
}
